import { FC, useMemo } from 'react';

import useFeedValidationSchema from './feedingValidationSchema';
import { Form, Formik } from 'formik';

import { ButtonType } from '../../../../utils/@globalTypes';
import AdultFeedingItem from './AdultFeedingItem';

import styles from './FeedingSettings.module.scss';
import Checkbox from '../../../../components/Checkbox';

import KidsFeedingBlock from './KidsFeedingBlock';
import Button from '../../../../components/Button';
import KidsFreeBlock from '../../../../components/KidsFreeBlock';
import useWindowDimensions from '../../../../utils/useWindowDimensions';
import { DeleteIcon } from '../../../../assets/icons';
import ScrollToError from '../../../../components/Booking/ScrollToError';
import { useDispatch } from 'react-redux';
import { updateFeedingSettings } from '../../../../redux/reducers/mainObjectSlice';
import {
  FeedingResponse,
  FeedingUpdateType,
  KidsFeegingType,
  Meal,
} from '../../../../redux/types/mainObjectTypes';
import HasKidsPrice from './HasKidsPrice';

type FeedingProps = {
  settings: FeedingResponse;
};
const FeedingSettings: FC<FeedingProps> = ({ settings }) => {
  // console.log(settings)
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const validationSchema = useFeedValidationSchema();
  const newKidsBlock: KidsFeegingType = {
    start_age: 0,
    end_age: null,
    kids_breakfast: '',
    kids_lunch: '',
    kids_dinner: '',
  };
  const initialValues = useMemo(
    () => ({
      isRequired: settings.is_required,
      has_breakfast: settings.breakfast?.has_meal ?? false,
      has_lunch: settings.lunch?.has_meal ?? false,
      has_dinner: settings.dinner?.has_meal ?? false,
      breakfast: settings.breakfast?.price ?? '',
      lunch: settings.lunch?.price ?? '',
      dinner: settings.dinner?.price ?? '',
      has_kidsPrice: settings.has_kids_price,
      ageKidsFree:
        settings.age_kids_free && settings.age_kids_free > 0
          ? settings.age_kids_free.toString()
          : '',
      kids: settings.kids_meals_prices ? settings.kids_meals_prices : [],
    }),
    [settings],
  );

  function compareAgeRanges(
    arr1: KidsFeegingType[],
    arr2: KidsFeegingType[],
  ): boolean {
    return (
      arr1.length === arr2.length &&
      arr1.every(
        (item, index) =>
          item.start_age === arr2[index].start_age &&
          item.end_age === arr2[index].end_age &&
          item.kids_breakfast === arr2[index].kids_breakfast &&
          item.kids_dinner === arr2[index].kids_dinner &&
          item.kids_lunch === arr2[index].kids_lunch,
      )
    );
  }
  function compareMeal(
    valuesHas_meal: boolean,
    valuesPrice: string,
    setingMeal: Meal,
  ): boolean {
    return (
      setingMeal?.has_meal &&
      valuesHas_meal === setingMeal.has_meal &&
      Number(valuesPrice) === Number(setingMeal.price)
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      enableReinitialize={true}
      onSubmit={(values) => {
        // console.log(values)
        if (settings.main_object) {
          const updateFeeding: FeedingUpdateType = {};
          if (settings.is_required !== values.isRequired) {
            updateFeeding.is_required = values.isRequired;
          }

          if (
            !compareMeal(
              values.has_breakfast,
              values.breakfast,
              settings.breakfast,
            )
          ) {
            updateFeeding.breakfast = {
              has_meal: values.has_breakfast,
              price: +values.breakfast > 0 ? values.breakfast : null,
            };
          }
          if (!compareMeal(values.has_lunch, values.lunch, settings.lunch)) {
            updateFeeding.lunch = {
              has_meal: values.has_lunch,
              price: +values.lunch > 0 ? values.lunch : null,
            };
          }
          if (!compareMeal(values.has_dinner, values.dinner, settings.dinner)) {
            updateFeeding.dinner = {
              has_meal: values.has_dinner,
              price: +values.dinner > 0 ? values.dinner : null,
            };
          }

          if (settings.has_kids_price !== values.has_kidsPrice) {
            updateFeeding.has_kids_price = values.has_kidsPrice;
          }
          if (Number(values.ageKidsFree) !== Number(settings.age_kids_free)) {
            updateFeeding.age_kids_free = Number(values.ageKidsFree);
          }
          if (!values.has_kidsPrice && Number(settings.age_kids_free) > 0) {
            updateFeeding.age_kids_free = null;
          }
          if (!values.has_kidsPrice && settings.kids_meals_prices.length > 0) {
            updateFeeding.kids_meals_prices = [];
          }

          if (!compareAgeRanges(values.kids, settings.kids_meals_prices)) {
            updateFeeding.kids_meals_prices = values.has_kidsPrice
              ? values.kids
              : [];
          }

          const keys = Object.keys(updateFeeding).length;
          if (keys > 0) {
            try {
              dispatch(
                updateFeedingSettings({
                  mainId: settings.main_object,
                  data: updateFeeding,
                }),
              );
            } catch (error) {
              console.error('Ошибка отправки:', error);
            }
          }
        }
      }}
    >
      {(formik) => {
        const addAgeRange = () => {
          const arrLength = formik.values.kids.length;
          if (
            formik.values.kids.length === 0 ||
            (arrLength > 0 && formik.values.kids[arrLength - 1].end_age)
          ) {
            const newBlock = { ...newKidsBlock };
            const newKids = [...formik.values.kids, newBlock];
            formik.setFieldValue('kids', newKids);
            formik.setFieldTouched('kids');
          }
        };

        return (
          <Form>
            <div className={styles.container}>
              <h4 className={styles.title}>Настройки стоимости питания</h4>
              <div className={styles.note}>
                При отсутствии питания перейдите к заполнению других вкладок
              </div>
              <div className={styles.note}>
                При наличии питания выберите необходимые приемы пищи
              </div>
              <div className={styles.note}>
                Настройки питания для детей можно ввести только при наличии
                общих настроек питания
              </div>
              <div className={styles.itemLine}>
                <Checkbox
                  checked={formik.values.isRequired}
                  label="Питание обязательно для гостя?"
                  name={'isRequired'}
                  onClick={() => {
                    formik.setFieldTouched('isRequired');
                    formik.setFieldValue(
                      'isRequired',
                      !formik.values.isRequired,
                    );
                  }}
                />
              </div>
              <div className={styles.adultContainer}>
                <AdultFeedingItem title="Завтрак" field="breakfast" />
                <AdultFeedingItem title="Обед" field="lunch" />
                <AdultFeedingItem title="Ужин" field="dinner" />
              </div>
              <HasKidsPrice />
              {formik.values.has_kidsPrice && (
                <div className={styles.kidsContainer}>
                  <KidsFreeBlock />

                  {formik.values.kids?.length > 0 &&
                    formik.values.kids.map((item, index) => {
                      return (
                        <div key={index}>
                          <KidsFeedingBlock
                            item={item}
                            index={index}
                            error={
                              formik.errors?.kids && formik.errors?.kids[index]
                            }
                          />
                        </div>
                      );
                    })}
                  <div className={styles.btnWrap}>
                    <div className={styles.btnAdd}>
                      <Button
                        title={
                          width > 600 ? 'Добавить возрастной диапазон' : '+'
                        }
                        type={ButtonType.PRIMARY_SMALL}
                        disabled={
                          (formik.values.kids.length > 0 &&
                            !formik.values.kids[formik.values.kids.length - 1]
                              ?.end_age) ||
                          (formik.values.kids.length > 0 &&
                            formik.values.kids[formik.values.kids.length - 1]
                              ?.end_age == 17) ||
                          formik.values.kids[formik.values.kids.length - 1]
                            ?.end_age == 16 ||
                          formik.values.ageKidsFree === '17'
                        }
                        onClick={() => {
                          addAgeRange();
                        }}
                      />
                    </div>
                    {formik.values.kids?.length > 0 && (
                      <div className={styles.btnRemove}>
                        <Button
                          title={
                            width > 600 ? (
                              'Удалить возрастной диапазон'
                            ) : (
                              <DeleteIcon />
                            )
                          }
                          type={ButtonType.SECONDARY_SMALL}
                          onClick={() => {
                            const newArr = [...formik.values.kids];
                            newArr.pop();
                            formik.setFieldValue('kids', newArr);
                          }}
                          confirmWindowText="Вы действительно хотите удалить?"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div
                className={styles.btnFormWrap}
                onClick={(e) => !formik.dirty && e.stopPropagation()}
              >
                <div
                  className={styles.btnSave}
                  onClick={() => !formik.dirty && formik.setSubmitting(false)}
                >
                  <Button
                    title="Сохранить"
                    disabled={!formik.dirty}
                    type={ButtonType.PRIMARY_SMALL}
                    actionType="submit"
                  />
                </div>
                <div className={styles.btnCancel}>
                  <Button
                    title="Отмена"
                    type={ButtonType.SECONDARY_SMALL}
                    actionType="reset"
                    onClick={formik.resetForm}
                  />
                </div>
              </div>
            </div>
            <ScrollToError />
          </Form>
        );
      }}
    </Formik>
  );
};

export default FeedingSettings;
